/**
 * Button con variantes del handoff.
 *
 * Sprint 11 v2 S11-V2-02. Replica `.cb-btn` + `.cb-btn--{variant}` +
 * tamaños del handoff (`docs/COMPONENTES.md § Botones`).
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'cream' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-orange text-white border-[0.5px] border-orange hover:bg-orange-light active:bg-orange-dark',
  secondary:
    'bg-transparent text-orange border-[0.5px] border-white/15 hover:bg-orange/10 hover:border-orange/20',
  ghost: 'bg-transparent text-orange border-none hover:text-orange-light',
  cream: 'bg-text-on-warning text-soft-warning border-none hover:bg-[#2A1700]',
  danger: 'bg-state-danger text-bg-page border-none',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'py-1 px-2 text-xs',
  md: 'py-2 px-3 text-sm',
  lg: 'py-3 px-5 text-base',
};

export function Button({
  variant,
  size = 'md',
  fullWidth = false,
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  // ghost ignora padding base (handoff lo define más compacto)
  const sizeClass = variant === 'ghost' ? 'py-1 px-2 text-sm' : SIZE_CLASSES[size];
  const widthClass = fullWidth ? 'w-full justify-center' : '';

  return (
    <button
      type={type}
      className={`inline-flex items-center gap-1 rounded-sm font-medium transition-all duration-base whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${VARIANT_CLASSES[variant]} ${sizeClass} ${widthClass} ${className}`.trim()}
      {...rest}
    >
      {children}
    </button>
  );
}
